import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Plus, X } from "lucide-react";
import FormCard from "../components/FormBuilder/FormCard";
import { useForms, useFormById } from "@/hooks/queries/useForms";
import { useDeleteForm } from "@/hooks/mutations/forms";
import Loader from "../components/Loader";
import { toast } from "sonner";
import useToastStore from "@/components/Common/Toast/toastStore";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";

// Import ReactQuill for rich text editor support:
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { formsService } from "@/services/formsService";

const Forms = () => {
  const [showFormModal, setShowFormModal] = useState(false);
  const [selectedFormId, setSelectedFormId] = useState(null);

  // State for delete confirmation modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [formToDelete, setFormToDelete] = useState(null);
  // Flag to indicate deletion (used to display Loader exclusively)
  const [isDeleting, setIsDeleting] = useState(false);

  const navigate = useNavigate();

  // Fetch all forms
  const { data: formsResponse, isLoading, isError } = useForms();
  const deleteFormMutation = useDeleteForm();
  const savedForms = formsResponse?.data || [];

  // Fetch the selected form details
  const { data: selectedFormResponse } = useFormById(selectedFormId, {
    enabled: !!selectedFormId,
  });
  const selectedForm = selectedFormResponse?.data;

  const handleViewForm = (formId) => {
    setSelectedFormId(formId);
    setShowFormModal(true);
  };

  const handleEditForm = (form) => {
    navigate(`/forms/builder/${form.id}`, { state: { form } });
  };

  // When a delete action is triggered, open the confirmation modal
  const handleDeleteForm = (formId) => {
    setFormToDelete(formId);
    setShowDeleteModal(true);
  };

  // When the user confirms deletion, show only the loader,
  // display a success toast message, and then refresh the page.
  const handleConfirmDelete = async () => {
    setIsDeleting(true);
    try {
      await deleteFormMutation.mutateAsync(formToDelete);
      toast.success("Form deleted successfully!");
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error) {
      console.error("Error deleting form:", error);
      setIsDeleting(false);
      setShowDeleteModal(false);
      setFormToDelete(null);
    }
  };

  // Duplicate form handler
  const handleDuplicateForm = async (form) => {
    try {
      await formsService.duplicateForm(form.id);
      toast.success("Form duplicated successfully!");
      // Optionally, refresh the forms list
      window.location.reload();
    } catch (error) {
      toast.error(error.message || "Failed to duplicate form");
    }
  };

  const closeModal = () => {
    setShowFormModal(false);
    setSelectedFormId(null);
  };

  // If the data is loading or deletion is in progress, display only the <Loader />
  if (isLoading || isDeleting) {
    return (
      <div className="flex min-h-screen w-full items-center justify-center bg-background">
        <Loader bgColor="transparent" loadingText="Loading forms..." />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="min-h-screen bg-muted p-4">
        Error loading forms. Please try again.
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-background">
      <div className="flex-1 w-[90%] pt-12  px-8 mx-auto flex flex-col gap-8">
        {/* Header Section */}
        <div className="flex flex-col gap-4">
          <div className="flex justify-between items-start flex-wrap gap-4">
            <div className="flex flex-col gap-1">
              <h1 className="text-foreground text-2xl font-semibold font-['Inter'] leading-8">
                Forms
              </h1>
              <p className="text-muted-foreground text-base font-normal font-['Inter'] leading-6">
                Manage your custom forms, build new data collection flows, and
                track form activity.
              </p>
            </div>
            <Link
              to="/forms/builder"
              className="bg-primary-dark h-[36px] text-white px-4 py-2.5 rounded-lg shadow-sm font-semibold transition-all hover:bg-primary-dark flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              Add Form
            </Link>
          </div>
        </div>

        {/* Forms List */}
        {savedForms.length === 0 ? (
          <div className="flex justify-center items-center h-[calc(100vh-200px)]">
            <h3 className="text-lg font-medium text-text-foreground">
              No Forms Found
            </h3>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {savedForms.map((form) => (
              <FormCard
                key={form.id}
                form={form}
                onView={() => handleViewForm(form.id)}
                onEdit={() => handleEditForm(form)}
                onDelete={() => handleDeleteForm(form.id)}
                onDuplicate={handleDuplicateForm}
              />
            ))}
          </div>
        )}
      </div>

      {/* Form Modal */}
      {showFormModal && selectedForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center overflow-y-auto">
          <div className="bg-card rounded-lg shadow-xl w-full max-w-4xl p-8 my-8 max-h-[calc(100vh-2rem)] overflow-y-auto">
            <div className="flex justify-between items-center border-b pb-4">
              <h2 className="text-2xl font-bold text-foreground">
                {selectedForm.title}
              </h2>
              <button
                onClick={closeModal}
                className="text-muted-foreground hover:text-foreground focus:outline-none"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <p className="mt-4 text-muted-foreground">{selectedForm.description}</p>
            <div className="mt-6">
              {selectedForm.fields.map((field, index) => (
                <div key={field.id || index} className="mb-4">
                  <label className="block text-sm font-medium text-foreground">
                    {field.label}
                    {field.is_required === "1" && (
                      <span className="text-red-500 ml-1">*</span>
                    )}
                  </label>
                  {field.sublabel && (
                    <p className="text-sm text-muted-foreground mb-1">
                      {field.sublabel}
                    </p>
                  )}
                  {/* Render different input types based on field.field_type */}
                  {field.field_type === "text" && (
                    <input
                      type="text"
                      placeholder={field.placeholder || ""}
                      className="mt-1 block w-full px-3 py-2 border border-input rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  )}
                  {field.field_type === "email" && (
                    <input
                      type="email"
                      placeholder={field.placeholder || ""}
                      className="mt-1 block w-full px-3 py-2 border border-input rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  )}
                  {/* Updated Phone Input Condition */}
                  {(field.field_type === "tel" ||
                    field.field_type === "phone" ||
                    field.field_type === "phone_number") && (
                    <PhoneInput
                      country="gb"
                      value={field.value || ""}
                      onChange={(value, country) => {
                        // Handle phone number change
                        console.log(
                          "Phone number changed:",
                          value,
                          "Country:",
                          country,
                        );
                      }}
                      placeholder="Enter phone number"
                      enableSearch={true}
                      searchPlaceholder="Search country..."
                      inputClass="mt-1 block w-full px-3 py-2 border border-input rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      containerClass="phone-input-container"
                      buttonClass="phone-input-button"
                      dropdownClass="phone-input-dropdown"
                    />
                  )}
                  {field.field_type === "url" && (
                    <input
                      type="url"
                      placeholder={field.placeholder || ""}
                      className="mt-1 block w-full px-3 py-2 border border-input rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  )}
                  {field.field_type === "file" && (
                    <input
                      type="file"
                      className="mt-1 block w-full px-3 py-2 border border-input rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  )}
                  {field.field_type === "number" && (
                    <input
                      type="number"
                      placeholder={field.placeholder || ""}
                      className="mt-1 block w-full px-3 py-2 border border-input rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  )}
                  {field.field_type === "date" && (
                    <input
                      type="date"
                      placeholder={field.placeholder || ""}
                      className="mt-1 block w-full px-3 py-2 border border-input rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  )}
                  {field.field_type === "textarea" && (
                    <textarea
                      placeholder={field.placeholder || ""}
                      className="mt-1 block w-full px-3 py-2 border border-input rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      rows={4}
                    />
                  )}
                  {field.field_type === "select" && (
                    <>
                      <select
                        className="mt-1 block w-full px-3 py-2 border border-input rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        defaultValue=""
                      >
                        {field.placeholder && (
                          <option value="" disabled>
                            {field.placeholder}
                          </option>
                        )}
                        {Array.isArray(field.options)
                          ? field.options.map((option, i) => (
                              <option
                                key={i}
                                value={option.value || option.label || option}
                              >
                                {option.label || option}
                              </option>
                            ))
                          : field.options.split(",").map((option, i) => (
                              <option key={i} value={option.trim()}>
                                {option.trim()}
                              </option>
                            ))}
                      </select>
                    </>
                  )}
                  {field.field_type === "checkbox" && (
                    <>
                      <div className="mt-2">
                        {field.options ? (
                          field.options?.split(",").map((option, i) => (
                            <div key={i} className="flex items-center">
                              <input
                                type="checkbox"
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-input rounded"
                              />
                              <label className="ml-2 text-sm text-foreground">
                                {option.trim()}
                              </label>
                            </div>
                          ))
                        ) : (
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-input rounded"
                            />
                          </div>
                        )}
                      </div>
                    </>
                  )}

                  {field.field_type === "radio" && (
                    <>
                      <div className="mt-2">
                        {field.options ? (
                          field.options?.split(",").map((option, i) => (
                            <div key={i} className="flex items-center">
                              <input
                                type="radio"
                                name={field.id}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-input"
                              />
                              <label className="ml-2 text-sm text-foreground">
                                {option.trim()}
                              </label>
                            </div>
                          ))
                        ) : (
                          <div className="flex items-center">
                            <input
                              type="radio"
                              name={field.id}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-input"
                            />
                          </div>
                        )}
                      </div>
                    </>
                  )}
                  {/* Rich Text Editor Case */}
                  {/* {field.field_type === "wysiwyg" && (
                    <ReactQuill
                      theme="snow"
                      value={field.value || ""}
                      placeholder={field.placeholder || ""}
                      onChange={(value) => {
                        // Optionally update the value in your state
                      }}
                    />
                  )} */}
                  {/* {spacer} */}
                  {field.field_type === "spacer" && (
                    <hr className="my-4 border-t border-border" />
                  )}
                </div>
              ))}
            </div>
            <div className="mt-4">
              <span className="font-semibold">
                Total Fields: {selectedForm.fields.length}
              </span>
            </div>
            <button
              onClick={closeModal}
              className="mt-4 bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div
          className="relative z-10"
          aria-labelledby="modal-title"
          role="dialog"
          aria-modal="true"
        >
          <div
            className="fixed inset-0 bg-foreground/40 transition-opacity"
            aria-hidden="true"
          ></div>
          <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <div className="relative transform overflow-hidden rounded-lg bg-card text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                <div className="bg-card px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    <div className="mx-auto flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                      <svg
                        className="h-6 w-6 text-red-600"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth="1.5"
                        stroke="currentColor"
                        aria-hidden="true"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"
                        />
                      </svg>
                    </div>
                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                      <h3
                        className="text-base font-semibold text-foreground"
                        id="modal-title"
                      >
                        Delete Form
                      </h3>
                      <div className="mt-2">
                        <p className="text-sm text-muted-foreground">
                          Are you sure you want to delete this form? This action
                          cannot be undone.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-muted px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                  <button
                    type="button"
                    onClick={handleConfirmDelete}
                    className="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-red-500 sm:ml-3 sm:w-auto"
                  >
                    Delete
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowDeleteModal(false)}
                    className="mt-3 inline-flex w-full justify-center rounded-md bg-card px-3 py-2 text-sm font-semibold text-foreground ring-1 shadow-xs ring-input ring-inset hover:bg-muted sm:mt-0 sm:w-auto"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Forms;
